import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

const BADGE_TONES = {
  success: { bg: 'rgba(58,232,105,0.12)', fg: '#3AE869', border: 'rgba(58,232,105,0.4)' },
  warning: { bg: 'rgba(255,123,0,0.12)',  fg: '#FFB073', border: 'rgba(255,123,0,0.4)' },
  error:   { bg: 'rgba(255,0,0,0.12)',    fg: '#FF6B6B', border: 'rgba(255,0,0,0.4)' },
  neutral: { bg: 'rgba(255,255,255,0.05)', fg: 'rgba(255,255,255,0.6)', border: 'rgba(255,255,255,0.12)' },
};

export default function BillingModalPlanSummary({ summary, isLoading }) {
  if (isLoading && !summary) return <SkeletonRows />;
  if (!summary) return null;

  const { plan, statusBadge, nextEventLabel, nextEventDate } = summary;
  const tone = BADGE_TONES[statusBadge?.tone] ?? BADGE_TONES.neutral;
  const formattedDate = nextEventDate ? new Date(nextEventDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  return (
    <View style={styles.root}>
      <View style={styles.row}>
        <Text style={styles.planName}>{plan?.name ?? 'Subscription'}</Text>
        <View
          style={[styles.badge, { backgroundColor: tone.bg, borderColor: tone.border }]}
          accessibilityLabel={`Subscription status: ${statusBadge?.label ?? 'unknown'}`}
        >
          <Text style={[styles.badgeText, { color: tone.fg }]}>{statusBadge?.label ?? '...'}</Text>
        </View>
      </View>
      {plan?.price && (
        <View style={styles.priceRow}>
          {plan.priceFull && plan.priceFull.amountFormatted !== plan.price.amountFormatted ? (
            <Text style={styles.priceFull}>{plan.priceFull.amountFormatted}</Text>
          ) : null}
          <Text style={styles.priceLine}>
            {plan.price.amountFormatted} / {plan.interval === 'annual' ? 'year' : 'month'}
          </Text>
          {plan.hasActiveDiscount ? (
            <Text style={styles.discountTag}>Founder&apos;s Rate</Text>
          ) : null}
        </View>
      )}
      {nextEventLabel && formattedDate && (
        <Text style={styles.nextEvent}>
          {nextEventLabel}, {formattedDate}
        </Text>
      )}
    </View>
  );
}

function SkeletonRows() {
  return (
    <View style={styles.root}>
      <View style={[styles.skeleton, { width: '40%', height: 20 }]} />
      <View style={[styles.skeleton, { width: '30%', height: 16, marginTop: 8 }]} />
      <View style={[styles.skeleton, { width: '60%', height: 14, marginTop: 10 }]} />
    </View>
  );
}

const styles = StyleSheet.create({
  root: { paddingHorizontal: 20, paddingVertical: 14 },
  row: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  planName: { fontSize: 16, fontWeight: '600', color: '#FFFFFF' },
  badge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, borderWidth: 1 },
  badgeText: { fontSize: 12, fontWeight: '600' },
  priceRow: { flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginTop: 6 },
  priceLine: { fontSize: 14, color: 'rgba(255,255,255,0.7)' },
  priceFull: { fontSize: 13, color: 'rgba(255,255,255,0.45)', textDecorationLine: 'line-through' },
  discountTag: { fontSize: 11, fontWeight: '600', color: '#9EBBFF', textTransform: 'uppercase', letterSpacing: 0.6 },
  nextEvent: { marginTop: 8, fontSize: 13, color: 'rgba(255,255,255,0.5)' },
  skeleton: { backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 4 },
});
